import { useEffect } from "react";
import { isEmpty } from "lodash";
import type { useHistory } from "react-router-dom";
import { getSearchPath } from "src/consts/urlPaths";
import useUpdateEffect from "src/hooks/useUpdateEffect";
import {
  filterTypes,
  positionOccupancyStatus,
  positionOccupancyStatusApiValues,
} from "../../../pages/SearchPage/SearchPage.config";
import { dedicationPeopleSearch } from "../../../pages/TeamDetailPage/tabs/TeamInsightsTab/dedicationPeoplePath";
import { gradeCompressionPeopleSearch } from "../../../pages/TeamDetailPage/tabs/TeamInsightsTab/gradeCompressionPeoplePath";
import { workerAttributePeopleSearch } from "../../../pages/TeamDetailPage/tabs/TeamInsightsTab/workerAttributePeoplePath";

const keptPeopleSearch = (search: string): string =>
  gradeCompressionPeopleSearch(search) ||
  dedicationPeopleSearch(search) ||
  workerAttributePeopleSearch(search);

export type FilterUrlHistory = ReturnType<typeof useHistory>;

export type FilterUrlState = {
  type: string | undefined;
  effectiveSearchTerm: string | null | undefined;
  peopleFilters: string[];
  defaultTeamTypes: string[];
  positionFilters: string[];
  positionStatus: string | undefined;
  includeHiddenTeams: boolean;
};

const buildFilteredSearchUrl = ({
  type,
  effectiveSearchTerm,
  peopleFilters,
  defaultTeamTypes,
  positionFilters,
  positionStatus,
  includeHiddenTeams,
}: FilterUrlState): string => {
  const url = getSearchPath(type, effectiveSearchTerm, false);
  const searchParams: string[] = [];
  if (!isEmpty(peopleFilters)) {
    searchParams.push(`filters=${peopleFilters.join()}`);
  }

  if (!isEmpty(defaultTeamTypes)) {
    searchParams.push(`team_types=${defaultTeamTypes.join()}`);
  }

  if (!isEmpty(positionFilters)) {
    searchParams.push(`position_filters=${positionFilters.join()}`);
  }

  if (positionStatus && positionStatus !== positionOccupancyStatus.ALL) {
    searchParams.push(`position_status=${positionStatus}`);
  }

  if (includeHiddenTeams) {
    searchParams.push("include_hidden=true");
  }

  return !isEmpty(searchParams) ? `${url}?${searchParams.join("&")}` : url;
};

/**
 * Pushes the current filter selection into the URL whenever it changes
 * (but not on mount - see useUpdateEffect), so the search page's filters
 * stay shareable/bookmarkable.
 *
 * Directory users never see the refine filters, so their URL is kept to
 * just the type/search term instead - any filters that made it into the
 * URL (e.g. a shared link) are dropped rather than reflected back.
 */
export const useSyncFiltersToUrl = (
  state: FilterUrlState & {
    isDirectoryUser: boolean;
    history: FilterUrlHistory;
  }
): void => {
  const {
    type,
    effectiveSearchTerm,
    peopleFilters,
    defaultTeamTypes,
    positionFilters,
    positionStatus,
    includeHiddenTeams,
    isDirectoryUser,
    history,
  } = state;

  useUpdateEffect(() => {
    if (isDirectoryUser) {
      return;
    }

    const built = buildFilteredSearchUrl({
      type,
      effectiveSearchTerm,
      peopleFilters,
      defaultTeamTypes,
      positionFilters,
      positionStatus,
      includeHiddenTeams,
    });
    // Arriving from View people sets filter state to empty, which must not
    // drop the grade-compression query. A real filter change leaves that mode.
    const kept = built.includes("?")
      ? ""
      : keptPeopleSearch(history?.location?.search ?? "");
    const currentUrl = kept ? `${built}${kept}` : built;
    const prevPath = history?.location?.search
      ? `${history?.location?.pathname}${history?.location?.search}`
      : history?.location?.pathname;

    if (decodeURIComponent(currentUrl) !== decodeURIComponent(prevPath)) {
      history.push(currentUrl);
    }
  }, [
    peopleFilters,
    defaultTeamTypes,
    positionFilters,
    positionStatus,
    includeHiddenTeams,
    type,
    effectiveSearchTerm,
    history,
    isDirectoryUser,
  ]);

  useUpdateEffect(() => {
    if (!isDirectoryUser) {
      return;
    }

    const currentUrl = getSearchPath(type, effectiveSearchTerm, false);
    if (decodeURI(currentUrl) !== decodeURI(history.location.pathname)) {
      history.push(currentUrl);
    }
  }, [type, effectiveSearchTerm, history, isDirectoryUser]);
};

export type ParseFiltersFromUrlArgs = {
  type: string | undefined;
  history: FilterUrlHistory;
  isDirectoryUser: boolean;
  canShowHiddenTeams: boolean;
  setPeopleFilters: (filters: string[]) => void;
  setDefaultTeamTypes: (teamTypes: string[]) => void;
  setPositionFilters: (filters: string[]) => void;
  setPositionStatus: (status: string) => void;
  setIncludeHiddenTeams: (includeHiddenTeams: boolean) => void;
};

/**
 * Parses URL query parameters on mount and when `type` changes.
 *
 * Directory users have no access to the refine filters, so any filters
 * that arrived in the URL (e.g. a bookmarked/shared link from before the
 * role changed) are reset instead of applied, and the URL is stripped
 * back down to just the type/search term.
 */
export const useParseFiltersFromUrl = ({
  type,
  history,
  isDirectoryUser,
  canShowHiddenTeams,
  setPeopleFilters,
  setDefaultTeamTypes,
  setPositionFilters,
  setPositionStatus,
  setIncludeHiddenTeams,
}: ParseFiltersFromUrlArgs): void => {
  useEffect(() => {
    if (isDirectoryUser) {
      setPeopleFilters([]);
      setDefaultTeamTypes([]);
      setPositionFilters([]);
      setPositionStatus(positionOccupancyStatus.ALL);
      setIncludeHiddenTeams(false);

      if (history.location.search) {
        const kept = keptPeopleSearch(history.location.search);
        const next = `${history.location.pathname}${kept}`;
        if (`${history.location.pathname}${history.location.search}` !== next) {
          history.replace(next);
        }
      }
      return;
    }

    const searchParams = new URLSearchParams(history.location.search);
    const filtersParam = searchParams.get("filters");
    const teamTypesParam = searchParams.get("team_types");
    const positionFiltersParam = searchParams.get("position_filters");
    const positionStatusParam = searchParams.get("position_status");
    const includeHiddenParam = searchParams.get("include_hidden");

    if (filtersParam) {
      const filters = filtersParam.split(",").filter(Boolean);
      setPeopleFilters(filters);
    } else {
      setPeopleFilters([]);
    }

    if (teamTypesParam) {
      const teamTypes = teamTypesParam.split(",").filter(Boolean);
      setDefaultTeamTypes(teamTypes);
    } else {
      setDefaultTeamTypes([]);
    }

    if (positionFiltersParam) {
      setPositionFilters(positionFiltersParam.split(",").filter(Boolean));
    } else {
      setPositionFilters([]);
    }

    if (
      positionStatusParam &&
      positionOccupancyStatusApiValues.includes(positionStatusParam)
    ) {
      setPositionStatus(positionStatusParam);
    } else {
      setPositionStatus(positionOccupancyStatus.ALL);
    }

    setIncludeHiddenTeams(
      canShowHiddenTeams &&
        includeHiddenParam === "true" &&
        type === filterTypes.TEAMS
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    isDirectoryUser,
    history,
    setPeopleFilters,
    setDefaultTeamTypes,
    setPositionFilters,
    setPositionStatus,
    setIncludeHiddenTeams,
    canShowHiddenTeams,
  ]);
};
